import React, { Children, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import * as FormActions from '../../actions/actionCreators';
import ProgressRing from './ProgressRing';
import { useAppSelector, useAppDispatch } from '../../hooks';
import { Select } from '@rmwc/select';
import { persistor, type RootState } from '../../store';

interface FormProgressStepProps {
  currentStep: number;
  children: React.ReactNode;
  index: number;
  formType: string;
  title: string;
  formIntro?: {
    title: string;
    content: string;
  };
  showFormIntro?: boolean;
}

interface FormChildProps {
  title: string;
  name: string;
  formType: string;
  isComplete?: boolean;
  sectionHeading?: string;
  index?: number;
}

const FormProgressStep: React.FC<FormProgressStepProps> = ({
  currentStep,
  children,
  index,
  formType,
  formIntro,
  showFormIntro: initialShowFormIntro = false,
}) => {
  const dispatch = useAppDispatch();
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const headerElmRef = useRef<HTMLElement | null>(null);
  const scrollToRef = useRef<HTMLDivElement | null>(null);

  const [isInvalid, setIsInvalid] = useState(false);

  // @ts-expect-error types not found
  const { currentIndex, unfinishedStep } = useAppSelector(
    (state: RootState) => state.forms
  );

  const [showFormIntro, setShowFormIntro] = useState(
    initialShowFormIntro && !currentIndex
  );

  const forms = useAppSelector((state: RootState) => state.forms);

  useEffect(() => {
    parentContainerRef.current = document.querySelector('.js--complaintform');
    headerElmRef.current = document.querySelector('.header--mobile');
  }, []);

  useEffect(() => {
    const formElm = document.querySelector('.step--open');
    if (formElm) {
      setIsInvalid(!!formElm.querySelector('[class*="--invalid"]'));
    }
  }, [currentStep]);

  useEffect(() => {
    // if !forms then purge the store
    if (!forms) {
      persistor.purge();
    }
  }, [forms]);

  const handleCloseIntro = () => {
    dispatch({ type: 'RESET_STORE' });
    persistor.purge();

    if (parentContainerRef.current) {
      parentContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });

      if (headerElmRef.current) {
        setTimeout(() => {
          headerElmRef.current?.classList.add('header__container--hidden');
        }, 500);
      }
    }

    setShowFormIntro(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const s = parseInt(e.target.value, 10);
    const optionElement = e.target.childNodes[index] as HTMLElement;
    const dataSet = optionElement.dataset;
    if (dataSet.name && dataSet.formtype) {
      dispatch(FormActions.editForm(dataSet.name, s, dataSet.formtype));
    }
  };

  const handleStepEdit = (
    elem: React.MouseEvent,
    child: React.ReactElement<FormChildProps>
  ) => {
    const { name, index: childIndex, formType: childFormType } = child.props;
    const currentStepIndex = currentStep === childIndex;
    const isUnfinishedStep = unfinishedStep === childIndex;

    let isComplete = false;

    if (forms && typeof forms[name] !== 'undefined') {
      isComplete = forms[name]?.isComplete || false;
    }

    let isCurrent = false;
    if (forms && typeof forms[name] !== 'undefined') {
      isCurrent = forms[name]?.isCurrent || false;
    }

    if (isInvalid) {
      if (unfinishedStep === currentStep) {
        if (index <= unfinishedStep) {
          dispatch(FormActions.editForm(name, childIndex, childFormType));
        }
      }
    } else if (currentStep <= unfinishedStep) {
      if (isComplete || isUnfinishedStep) {
        if (
          (!isCurrent && isComplete) ||
          currentStepIndex ||
          isUnfinishedStep
        ) {
          dispatch(FormActions.editForm(name, childIndex, childFormType));
        }
      }
    }
  };

  const arrayChildren = Children.toArray(children);
  const progressPercent = (currentStep / (arrayChildren.length - 1)) * 100;

  const isLast = currentStep === arrayChildren.length - 1;

  const stepOptions = useMemo(() => {
    return arrayChildren.map((child, i) => {
      const {
        title,
        name,
        formType: childType,
      } = (child as React.ReactElement<FormChildProps>).props;
      const labelIndex = i + 1;

      let isDisabled = true;

      const isUnfinishedStep = unfinishedStep === index;

      let isComplete = false;

      if (forms && typeof forms[name] !== 'undefined') {
        isComplete = forms[name]?.isComplete || false;
      }

      let isCurrent = false;
      if (forms && typeof forms[name] !== 'undefined') {
        isCurrent = forms[name]?.isCurrent || false;
      }

      if (index <= unfinishedStep) {
        isDisabled = false;
        if (isComplete || isUnfinishedStep) {
          if (currentStep < unfinishedStep && isInvalid) {
            isDisabled = true;
            if (isCurrent) {
              isDisabled = false;
            }
          }
        }
      }

      const optionLabel = `${labelIndex}: ${title}`;
      return {
        value: i,
        label: optionLabel,
        'data-formtype': childType,
        'data-name': name,
        key: i,
        disabled: isDisabled,
      };
    });
  }, [arrayChildren, forms, currentStep, isInvalid, unfinishedStep]);

  return (
    <div>
      {showFormIntro && formIntro && (
        <div className="form-stepper-container">
          <div className="form-stepper-intro step step--open">
            <div className="step__content">
              <h3>{formIntro.title}</h3>
              <div dangerouslySetInnerHTML={{ __html: formIntro.content }} />
              <div className="form-actions">
                <button
                  className="step__button button button--primary button--align-right"
                  type="button"
                  onClick={handleCloseIntro}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {!showFormIntro && (
        <>
          <div className="form-stepper-wrap" id="form-stepper-wrap">
            <div className="form-stepper form-stepper-ring">
              <ProgressRing
                radius={40}
                stroke={10}
                progress={progressPercent}
              />
              <div className="form-stepper-select">
                <label htmlFor="step-select" className="sr-only">
                  Choose a step:
                </label>
                <Select
                  name="step-select"
                  id="step-select"
                  className="step-select--long step-select--bold"
                  value={currentIndex?.toString() || '0'}
                  onChange={handleChange}
                  options={stepOptions.map((o) => ({
                    value: o.value.toString(),
                    label: o.label,
                  }))}
                />
              </div>
            </div>
            <ul className="form-stepper form-stepper-horizontal text-center mx-auto pl-0">
              {arrayChildren.map((child, i) => {
                const { name } = (child as React.ReactElement<FormChildProps>)
                  .props;
                let isCurrent = false;
                let isComplete = false;
                let title = '';

                if (forms && forms[name]) {
                  isCurrent = forms[name].isCurrent;
                  isComplete = forms[name].isComplete;
                  title = forms[name].sectionHeading;
                }

                const stepClass = classNames({
                  'form-stepper--unfinished': unfinishedStep === i,
                  'form-stepper--active': isCurrent,
                  'form-stepper--completed': isComplete,
                });

                if (!title) {
                  console.error(
                    `FormProgressStep: Missing title for step ${i + 1}. ${name}`
                  );
                }

                return (
                  <React.Fragment key={name}>
                    {title && (
                      <li
                        className={`${stepClass} text-center form-stepper-list`}
                      >
                        <button
                          className="mx-2"
                          type="button"
                          aria-label={`Go to step ${i + 1}: ${title}`}
                          onClick={(elem) =>
                            handleStepEdit(
                              elem,
                              child as React.ReactElement<FormChildProps>
                            )
                          }
                        >
                          <span className="form-stepper-circle">
                            <span>
                              {isComplete && isLast ? (
                                <i className="step__icon">
                                  <svg
                                    height="15"
                                    width="15"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 490 490"
                                    fill="#ffffff"
                                  >
                                    <path
                                      d="M452.253 28.326 197.831 394.674 29.044 256.875 0 292.469l207.253 169.205L490 54.528z"
                                      stroke="#ffffff"
                                      strokeWidth="25"
                                    />
                                  </svg>
                                </i>
                              ) : (
                                i + 1
                              )}
                            </span>
                          </span>
                          <div className="label">{title}</div>
                        </button>
                      </li>
                    )}
                  </React.Fragment>
                );
              })}
            </ul>
          </div>

          <div className="form-stepper-container">
            {arrayChildren.map((child, formIndex) => {
              const { name } = (child as React.ReactElement<FormChildProps>)
                .props;

              let isComplete = false;
              let isCurrent = false;

              if (forms && forms[name]) {
                isCurrent = forms[name].isCurrent;
                isComplete = forms[name].isComplete;
              }

              const stepClass = classNames({
                step: true,
                'step--open': formType === 'contact' ? true : isCurrent,
                'step--complete': formType === 'contact' ? true : isComplete,
              });

              if (!isCurrent) {
                return null;
              }

              return (
                <div key={name} className={stepClass} ref={scrollToRef}>
                  <div className="step__content">
                    {React.cloneElement(child as React.ReactElement, {
                      name,
                      formIndex,
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default FormProgressStep;
